import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useSubscription } from "@/contexts/subscription-context";
import { useBrand } from "@/contexts/brand-context";
import { pb } from "@/lib/pocketbase";
import { useSearchParams } from "react-router-dom";
import {
  Organizations,
  SubscriptionsPlanOptions,
  SubscriptionsStatusOptions,
  Users,
} from "@/types/pocketbase-types";
import { toast } from "sonner";
import {
  createSubscription,
  cancelSubscription,
  StripeProduct,
  StripePrice,
} from "@/services/payments";
import { PricingPlansGrid } from "@/components/billing/PricingPlansGrid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

export default function Settings() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const { currentBrand } = useBrand();
  const [userData, setUserData] = useState<Users | null>(null);
  const [orgData, setOrgData] = useState<Organizations | null>(null);

  // Get tab from URL query params, default to "account"
  const defaultTab = searchParams.get("tab") || "account";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organizationName: "",
  });
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [products] = useState<StripeProduct[]>([]);
  const [prices] = useState<StripePrice[]>([]);
  const [plansLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Cancellation form state
  const [cancelReasons, setCancelReasons] = useState<string[]>([]);
  const [expectationsMet, setExpectationsMet] = useState<string>("");
  const [improvementFeedback, setImprovementFeedback] = useState("");

  // Helper functions for subscription data
  const getPlanDisplayName = () => {
    if (!subscription) return "No Active Subscription";

    switch (subscription.plan) {
      case SubscriptionsPlanOptions.Senior:
        return "Senior Creator Plan";
      case SubscriptionsPlanOptions.Junior:
        return "Junior Creator Plan";
      case SubscriptionsPlanOptions.Intern:
        return "Intern Creator Plan";
      case SubscriptionsPlanOptions.Free:
        return "Free Plan";
      default:
        return "Creator Plan";
    }
  };

  const getPlanPrice = () => {
    if (!subscription) return "No active subscription";
    switch (subscription.plan) {
      case SubscriptionsPlanOptions.Senior:
        return "$99/month • 50 articles per month";
      case SubscriptionsPlanOptions.Junior:
        return "$49/month • 20 articles per month";
      case SubscriptionsPlanOptions.Intern:
        return "$19/month • 5 articles per month";
      case SubscriptionsPlanOptions.Free:
        return "$0/month • 3 articles";
      default:
        return "Custom plan";
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const getUsagePercentage = () => {
    if (!subscription?.articlesQuota) return 0;
    const { used, limit } = subscription.articlesQuota;
    return limit > 0 ? Math.round((used / limit) * 100) : 0;
  };

  // Cancellation form handlers
  const handleReasonChange = (reason: string, checked: boolean) => {
    setCancelReasons((prev) => {
      if (checked) {
        return [...prev, reason];
      } else {
        return prev.filter((r) => r !== reason);
      }
    });
  };

  const resetCancelForm = () => {
    setCancelReasons([]);
    setExpectationsMet("");
    setImprovementFeedback("");
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.id) {
      toast.error("No subscription found to cancel");
      return;
    }

    // Validate form
    if (cancelReasons.length === 0) {
      toast.error("Please select at least one reason for cancelling");
      return;
    }

    if (!expectationsMet) {
      toast.error("Please select how well the product met your expectations");
      return;
    }

    if (cancelReasons.includes("other") && !improvementFeedback.trim()) {
      toast.error("Please provide feedback when selecting 'Other' as a reason");
      return;
    }

    setCancelLoading(true);
    try {
      const response = await cancelSubscription({
        subscriptionId: subscription.id,
        reasons: cancelReasons,
        expectationsMet: expectationsMet as "not_at_all" | "somewhat" | "fully",
        improvementFeedback: improvementFeedback.trim() || undefined,
      });

      toast.success(response.message);
      setShowCancelModal(false);
      resetCancelForm();

      // Refresh subscription data
      // Note: This should trigger a refresh of the subscription context
      window.location.reload(); // Temporary solution - ideally refresh context
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error("Failed to cancel subscription. Please try again.");
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePlanAction = async (productId: string) => {
    if (!user || !currentBrand) {
      toast.error("User or brand information missing");
      return;
    }

    // Find the product and get its details
    const product = products.find((p) => p.product_id === productId);
    if (!product) {
      toast.error("Plan not found");
      return;
    }

    const planId = product.metadata.plan_id || product.name.toLowerCase();

    // Get prices for this product
    const productPrices = prices.filter(
      (price) => price.product_id === productId,
    );
    const monthlyPrice = productPrices.find(
      (price) => price.interval === "month",
    );

    if (!monthlyPrice) {
      toast.error("Pricing information not available");
      return;
    }

    try {
      if (subscription) {
        // Handle plan change for existing subscribers
        // await changePlan({
        //   subscriptionId: subscription.id,
        //   planId: planId,
        //   billingType: "monthly",
        // });

        // toast.success("Plan changed successfully!");
        // await refreshSubscription();
        setShowPlanModal(false);
      } else {
        // Handle new subscription
        const subscriptionResponse = await createSubscription({
          planId: planId,
          priceId: monthlyPrice.id,
          billingType: "monthly",
          customerEmail: user.email,
          customerName: user.name || user.email.split("@")[0],
          orgId: user.org || "",
          redirectUrl: `${window.location.origin}/payment-response`,
          state: "billing",
        });

        // Redirect to payment URL
        if (subscriptionResponse.paymentUrl) {
          window.location.href = subscriptionResponse.paymentUrl;
        } else {
          throw new Error("No payment URL received");
        }
      }
    } catch (error) {
      console.error("Error processing plan selection:", error);
      toast.error(
        `Failed to ${subscription ? "change plan" : "create subscription"}. Please try again.`,
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user?.id) {
          // Fetch user data
          const userRecord = await pb
            .collection("users")
            .getOne<Users>(user.id, {
              expand: "org",
            });
          setUserData(userRecord);

          // Fetch organization data
          const orgRecord = userRecord?.expand?.org || null;
          if (orgRecord) {
            setOrgData(orgRecord);

            // Set form data
            setFormData({
              name: userRecord.name || "",
              email: userRecord.email || "",
              organizationName: orgRecord.name || "",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load user data");
      }
    };

    fetchData();
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update user data
      if (user?.id && formData.name !== userData?.name) {
        await pb.collection("users").update(user.id, {
          name: formData.name,
        });
      }

      // Update organization data
      if (userData?.org && formData.organizationName !== orgData?.name) {
        await pb.collection("organizations").update(userData.org, {
          name: formData.organizationName,
        });
      }

      toast.success("Settings updated successfully");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Settings</h2>
          <p className="text-muted-foreground">
            Manage your account and application preferences.
          </p>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="mb-8">
        <TabsList>
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          {/* <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger> */}
        </TabsList>

        <TabsContent value="account" className="pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Profile Settings</CardTitle>
              <CardDescription>
                Update your account information and preferences.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organizationName">Organization Name</Label>
                  <Input
                    id="organizationName"
                    value={formData.organizationName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* <Separator /> */}
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button variant="outline" className="mr-2">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading} variant="default">
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="pt-4">
          <div className="space-y-6">
            {/* Current Plan */}
            <Card>
              <CardHeader>
                <CardTitle>Current Plan</CardTitle>
                <CardDescription>
                  Manage your subscription and billing details.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscriptionLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h3 className="font-medium">{getPlanDisplayName()}</h3>
                        <p className="text-sm text-muted-foreground">
                          {getPlanPrice()}
                        </p>
                        {subscription?.status === "active" &&
                        subscription?.cancelEndDate == "" ? (
                          <p className="text-sm text-green-600">
                            Active until{" "}
                            {formatDate(subscription.nextBillingDate)}
                          </p>
                        ) : subscription?.status === "active" &&
                          subscription?.cancelEndDate &&
                          subscription?.cancelEndDate !== "" ? (
                          <p className="text-sm text-red-600">
                            Subscription cancels on{" "}
                            {formatDate(subscription.cancelEndDate)}
                          </p>
                        ) : subscription?.status === "trialing" ? (
                          <p className="text-sm text-blue-600">
                            Trial active
                            {subscription.trial?.daysRemaining !== undefined
                              ? ` - ${subscription.trial.daysRemaining} days remaining`
                              : ""}
                          </p>
                        ) : subscription?.status ===
                          SubscriptionsStatusOptions.Expired ? (
                          <p className="text-sm text-orange-600">
                            Subscription expired
                          </p>
                        ) : !subscription ? (
                          <p className="text-sm text-orange-600">
                            No active subscription
                          </p>
                        ) : (
                          <p className="text-sm text-gray-600">
                            Status: {subscription?.status || "Unknown"}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {/*<Button variant="outline" onClick={handleChangePlan}>
                          {!subscription ? "Subscribe Now" : "Change Plan"}
                        </Button>*/}
                        {subscription &&
                          subscription.status !==
                            SubscriptionsStatusOptions.Cancelled &&
                          subscription.status !==
                            SubscriptionsStatusOptions.Expired &&
                          !subscription.cancelAtPeriodEnd && (
                            <Button
                              variant="link"
                              onClick={() => setShowCancelModal(true)}
                              // className="text-red-600 border-red-200 hover:bg-red-50"
                              className="hover:text-red-500 text-red-400 opacity-90"
                            >
                              Cancel Subscription
                            </Button>
                          )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h3 className="font-medium">Usage this month</h3>
                        <p className="text-sm text-muted-foreground">
                          {subscription?.articlesQuota
                            ? `${subscription.articlesQuota.used} of ${subscription.articlesQuota.limit} articles used`
                            : "No active subscription"}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">
                          {getUsagePercentage()}%
                        </div>
                        <div className="w-20 h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                            style={{ width: `${getUsagePercentage()}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* <TabsContent value="seo" className="pt-4">
          <Card>
            <CardHeader>
              <CardTitle>SEO Settings</CardTitle>
              <CardDescription>
                Configure your SEO analysis and monitoring preferences.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Target Keywords</h3>
                <div className="space-y-2">
                  <Label htmlFor="primaryKeywords">Primary Keywords</Label>
                  <Input
                    id="primaryKeywords"
                    placeholder="Enter keywords, separated by commas"
                  />
                  <p className="text-sm text-muted-foreground">
                    These keywords will be prioritized in content analysis.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondaryKeywords">Secondary Keywords</Label>
                  <Input
                    id="secondaryKeywords"
                    placeholder="Enter keywords, separated by commas"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Competitor Tracking</h3>
                <div className="space-y-2">
                  <Label htmlFor="competitors">Competitor Websites</Label>
                  <Input
                    id="competitors"
                    placeholder="Enter URLs, separated by commas"
                  />
                  <p className="text-sm text-muted-foreground">
                    Monitor these sites for content and keyword strategies.
                  </p>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">SEO Audit Schedule</h3>
                <div className="space-y-2">
                  <Label htmlFor="auditFrequency">Audit Frequency</Label>
                  <Select defaultValue="weekly">
                    <SelectTrigger id="auditFrequency">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Auto-fix Simple Issues</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically fix minor SEO issues when detected.
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button variant="outline" className="mr-2">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-creator-500 hover:bg-creator-600"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Content Settings</CardTitle>
              <CardDescription>
                Configure your content creation and optimization preferences.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Content Analysis</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Readability Analysis</p>
                    <p className="text-sm text-muted-foreground">
                      Analyze and score content readability.
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Keyword Density Check</p>
                    <p className="text-sm text-muted-foreground">
                      Monitor keyword usage and flag over-optimization.
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Grammar Check</p>
                    <p className="text-sm text-muted-foreground">
                      Check content for grammatical issues.
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">AI Content Generation</h3>
                <div className="space-y-2">
                  <Label htmlFor="contentTone">Default Content Tone</Label>
                  <Select defaultValue="professional">
                    <SelectTrigger id="contentTone">
                      <SelectValue placeholder="Select tone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="authoritative">
                        Authoritative
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Smart Content Suggestions</p>
                    <p className="text-sm text-muted-foreground">
                      Get AI suggestions for content improvements.
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Content Gap Detection</p>
                    <p className="text-sm text-muted-foreground">
                      Identify missing content opportunities based on competitor
                      analysis.
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button variant="outline" className="mr-2">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-creator-500 hover:bg-creator-600"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Integrations</CardTitle>
              <CardDescription>
                Connect with external tools and platforms.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-6 w-6 text-green-600"
                        fill="currentColor"
                      >
                        <path d="M18 2a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h12m-6 6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3m0 8a5 5 0 0 1-5-5 5 5 0 0 1 5-5 5 5 0 0 1 5 5 5 5 0 0 1-5 5Z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium">Google Analytics</h3>
                      <p className="text-sm text-muted-foreground">
                        Connect your Analytics account to track performance.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">
                      Disconnect
                    </Button>
                    <div className="flex h-4 w-4 items-center justify-center rounded-full bg-green-100">
                      <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-6 w-6 text-blue-600"
                        fill="currentColor"
                      >
                        <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05-.78-.83-1.9-1.36-3.16-1.36-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98-3.56-.18-6.73-1.89-8.84-4.48-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23Z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium">Twitter</h3>
                      <p className="text-sm text-muted-foreground">
                        Connect your Twitter account for social sharing.
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Connect
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-6 w-6 text-orange-600"
                        fill="currentColor"
                      >
                        <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium">Facebook</h3>
                      <p className="text-sm text-muted-foreground">
                        Connect your Facebook account for social sharing.
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Connect
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-6 w-6 text-gray-600"
                        fill="currentColor"
                      >
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium">Google Search Console</h3>
                      <p className="text-sm text-muted-foreground">
                        Connect to import search performance data.
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Connect
                  </Button>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-creator-500 hover:bg-creator-600"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent> */}
      </Tabs>

      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {!subscription ? "Choose Your Plan" : "Change Your Plan"}
            </DialogTitle>
            <DialogDescription>
              {!subscription
                ? "Select the plan that best fits your content creation needs"
                : "Select a new plan for your subscription"}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            {plansLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <span className="ml-2">Loading pricing plans...</span>
              </div>
            ) : products.length > 0 ? (
              <PricingPlansGrid
                products={products}
                prices={prices}
                mode="billing"
                onPlanAction={handlePlanAction}
                className="max-w-4xl mx-auto"
              />
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  No pricing plans available at the moment.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cancel Your Subscription</DialogTitle>
            <DialogDescription>
              We're sorry to see you go! Please help us understand why you're
              cancelling so we can improve our service.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-6">
            {/* Cancellation Reasons */}
            <div className="space-y-3">
              <Label className="text-lg font-medium">
                Why are you cancelling? (Select all that apply)
              </Label>
              <div className="space-y-3">
                {[
                  {
                    id: "no_time_to_try",
                    label: "Didn't have enough time to try the product",
                  },
                  {
                    id: "insufficient_value",
                    label: "Didn't see enough value",
                  },
                  { id: "too_expensive", label: "Too expensive for my needs" },
                  {
                    id: "missing_features",
                    label: "Missing features I expected",
                  },
                  {
                    id: "too_complicated",
                    label: "Too complicated / hard to use",
                  },
                  {
                    id: "one_time_use",
                    label: "I only needed it for a one-time use",
                  },
                  { id: "other", label: "Other" },
                ].map((reason) => (
                  <div
                    key={reason.id}
                    className="flex items-center space-x-2 text-muted-foreground"
                  >
                    <Checkbox
                      id={reason.id}
                      checked={cancelReasons.includes(reason.id)}
                      onCheckedChange={(checked) =>
                        handleReasonChange(reason.id, checked as boolean)
                      }
                      className="text-muted-foreground"
                    />
                    <Label htmlFor={reason.id} className="text-sm leading-none">
                      {reason.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Expectations Met */}
            <div className="space-y-3">
              <Label className="text-lg font-medium">
                How well did the product meet your expectations?
              </Label>
              <RadioGroup
                value={expectationsMet}
                onValueChange={setExpectationsMet}
                className="text-muted-foreground"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="not_at_all" id="not_at_all" />
                  <Label htmlFor="not_at_all" className="text-muted-foreground">
                    Not at all
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="somewhat" id="somewhat" />
                  <Label htmlFor="somewhat">Somewhat</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="fully" id="fully" />
                  <Label htmlFor="fully">Fully</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Improvement Feedback */}
            <div className="space-y-2">
              <Label
                htmlFor="improvement_feedback"
                className="text-lg font-medium"
              >
                What can we do better?
                {cancelReasons.includes("other") && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
              <Textarea
                id="improvement_feedback"
                placeholder="Tell us how we can improve our service..."
                value={improvementFeedback}
                onChange={(e) => setImprovementFeedback(e.target.value)}
                rows={3}
              />
            </div>

            {/* Warning Message */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Before you cancel
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    {subscription?.status ===
                    SubscriptionsStatusOptions.Trialing ? (
                      <p>
                        Your trial will be cancelled immediately and you'll lose
                        access to all features.
                      </p>
                    ) : (
                      <p>
                        Your subscription will remain active until the end of
                        your current billing period. You can continue using all
                        features until then.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelModal(false);
                resetCancelForm();
              }}
              disabled={cancelLoading}
            >
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
            >
              {cancelLoading ? "Cancelling..." : "Cancel Subscription"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
